import React, { useState } from 'react';
import { Tooltip, Popconfirm, Dropdown, Modal, Upload, Button, message } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import { DeleteOutlined, MoreOutlined, CloudUploadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import "./styles.scss";
import { User, Activity, FileText, BarChart3, Bot, UploadCloud } from 'lucide-react';
import { Link } from "react-router-dom";
import axios from 'axios';
import { API_URL } from 'env';


interface AccountListItem {
  idAccount: number;
  accountId: string;
  initial_balance: number;
  current_balance_formatted: string;
  current_equity_formatted: string;
  current_pnl_formatted: string;
  todays_profit_formatted: string;
  current_equity: number;
  current_balance: number;
  current_pnl: number;
  todays_profit: number;
  created_at: string;
  status: 'active' | 'inactive' | 'suspended';
  platform: string;
  user: {
    userId: string;
    userName: string;
    broker: string;
  };
}

interface AccountListViewProps {
  accounts: AccountListItem[];
  onEdit?: (account: AccountListItem) => void;
  onDelete: (id: number, platform: string, account_number: string) => void;
}

const AccountListView: React.FC<AccountListViewProps> = ({ accounts, onEdit, onDelete }) => {
  const { t } = useTranslation();
  console.log("Account", accounts )
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [file, setFile] = useState<UploadFile | null>(null);
  const [isImporting, setIsImporting] = useState(false);

  const handleImport = async () => {
    if (!file || !selectedAccountId) {
      message.error('Please select a file');
      return;
    }
    setIsImporting(true);
    const formData = new FormData();
    if (file.originFileObj) {
      formData.append('excel_file', file.originFileObj);
    } else {
      message.error('Invalid file');
      setIsImporting(false);
      return;
    }
    formData.append('account_number', selectedAccountId);

    try {
      const response = await axios.post(
        `${API_URL}/api/v1/be/trade-history/import`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      if (response.data.success) {
        message.success('Import successful');
        setIsModalOpen(false);
        setFile(null);
      } else {
        message.error(response.data.message || 'Import failed');
      }
    } catch (error) {
      message.error('An error occurred during import');
    } finally {
      setIsImporting(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const getStatusBadgeClass = (status: AccountListItem['status']) => {
    if (status === 'active') return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
    if (status === 'inactive') return 'bg-slate-100 text-slate-600 border border-slate-200';
    return 'bg-amber-50 text-amber-700 border border-amber-200';
  };

  return (
    <div style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: '12px', overflowX: 'auto', boxShadow: '0 1px 2px rgba(16,24,40,0.03)' }}>
      <div style={{ minWidth: '920px' }}>
        {/* Header row */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '2.3fr 1.3fr 1.3fr 0.9fr 0.6fr 0.5fr',
          padding: '11px 18px',
          background: 'var(--bg-sub)',
          borderBottom: '1px solid var(--card-border)',
          gap: '8px'
        }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-desc)', letterSpacing: '0.4px', textTransform: 'uppercase' }}>{t('Account')}</div>
          <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-desc)', letterSpacing: '0.4px', textTransform: 'uppercase' }}>{t('Balance')} / {t('Equity')}</div>
          <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-desc)', letterSpacing: '0.4px', textTransform: 'uppercase' }}>{t('Todayprofit')} / {t('UnrealizedPnL')}</div>
          <div></div>
          <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-desc)', letterSpacing: '0.4px', textTransform: 'uppercase' }}>HIỂN THỊ</div>
          <div></div>
        </div>

        <div className="divide-y divide-gray-100">
          {accounts.map((account) => (
            <div
              key={account.idAccount}
              style={{ borderBottom: '1px solid var(--bg-divider)', position: 'relative' }}
              className="account-row-hover transition-colors"
            >
              <div style={{
                display: 'grid',
                gridTemplateColumns: '2.3fr 1.3fr 1.3fr 0.9fr 0.6fr 0.5fr',
                alignItems: 'center',
                padding: '13px 18px 5px',
                gap: '8px'
              }}>
                {/* Account info */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '11px', minWidth: 0 }}>
                  <div
                    style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      background: 'var(--bg-divider)',
                      color: 'var(--text-desc)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: '13px',
                      flexShrink: 0
                    }}
                  >
                    {(account.user?.userName || 'T')?.[0]?.toUpperCase()}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '7px', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 700, fontSize: '13.5px', color: 'var(--text-title)' }}>
                        {account.user?.userName || 'Trader'}
                      </span>
                      <span
                        style={{
                          background: 'var(--stat-teal-bg)',
                          color: 'var(--stat-teal-text)',
                          fontSize: '10px',
                          fontWeight: 700,
                          padding: '2px 6px',
                          borderRadius: '5px',
                          flexShrink: 0,
                          letterSpacing: '0.2px'
                        }}
                      >
                        #{account.platform?.toUpperCase() || 'MT5'}
                      </span>
                    </div>
                    <div className="om-num" style={{ fontSize: '12px', color: 'var(--text-desc)', marginTop: '2px' }}>
                      Login {account.accountId}
                    </div>
                  </div>
                </div>

                {/* Balance & Equity */}
                <div>
                  <div className="om-num" style={{ fontWeight: 700, fontSize: '13px', color: 'var(--text-title)' }}>
                    {account.current_balance_formatted}
                  </div>
                  <div className="om-num" style={{ fontWeight: 600, fontSize: '11.5px', color: 'var(--stat-teal-text)', marginTop: '2px' }}>
                    {account.current_equity_formatted}
                  </div>
                </div>

                {/* Profit & PnL */}
                <div>
                  <div className="om-num" style={{ fontWeight: 700, fontSize: '13px', color: 'var(--text-title)' }}>
                    {account.todays_profit_formatted || '$0.00'}
                  </div>
                  <div
                    className="om-num"
                    style={{
                      fontWeight: 700,
                      fontSize: '13px',
                      marginTop: '2px',
                      color: account.current_pnl < 0 ? 'var(--stat-red-text)' : account.current_pnl > 0 ? 'var(--stat-green-text)' : 'var(--text-title)'
                    }}
                  >
                    {account.current_pnl_formatted || '$0.00'}
                  </div>
                </div>

                <div></div>

                {/* Toggle Visibility */}
                <div>
                  <div style={{ width: '34px', height: '18px', background: '#f2a93b', borderRadius: '10px', position: 'relative', cursor: 'pointer' }}>
                    <div style={{ width: '14px', height: '14px', background: '#fff', borderRadius: '50%', position: 'absolute', top: '2px', right: '2px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}></div>
                  </div>
                </div>

                {/* Actions */}
                <div style={{ position: 'relative', display: 'flex', justifyContent: 'flex-end' }}>
                  <Dropdown
                    menu={{
                      items: [
                        {
                          key: 'edit',
                          label: (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9"></path><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z"></path></svg>
                              {t('EditAccount')}
                            </div>
                          ),
                          onClick: () => onEdit?.(account)
                        },
                        {
                          key: 'import',
                          label: (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <UploadCloud size={15} />
                              {t('ImportTradeHistory')}
                            </div>
                          ),
                          onClick: () => {
                            setSelectedAccountId(account.accountId);
                            setIsModalOpen(true);
                          }
                        },
                        {
                          type: 'divider'
                        },
                        {
                          key: 'delete',
                          danger: true,
                          label: (
                            <Popconfirm
                              title={t('DeleteAccount')}
                              description={t('DeleteAccountConfirm')}
                              onConfirm={() => onDelete(account.idAccount, account.platform, account.accountId)}
                              okText="Yes"
                              cancelText="No"
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18"></path><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path><path d="M10 11v6M14 11v6"></path></svg>
                                {t('DeleteAccount')}
                              </div>
                            </Popconfirm>
                          )
                        }
                      ]
                    }}
                    trigger={['click']}
                  >
                    <button
                      style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '7px',
                        border: '1px solid var(--card-border)',
                        background: 'var(--card-bg)',
                        color: 'var(--text-desc)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      className="hover:bg-[var(--bg-hover)] transition-colors"
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="1.8"></circle><circle cx="12" cy="12" r="1.8"></circle><circle cx="19" cy="12" r="1.8"></circle></svg>
                    </button>
                  </Dropdown>
                </div>
              </div>

              {/* Quick Links */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '0 18px 11px 65px' }}>
                <Link
                  to={`/academy?tab=checklist&date=${(() => { const d = new Date(); return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`; })()}&step=0`}
                  style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', fontWeight: 600, color: 'var(--text-title)', textDecoration: 'none' }}
                  className="hover:text-[var(--stat-teal-text)] transition-colors"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 3v18h18"></path><path d="M18 17V9M13 17V5M8 17v-3"></path></svg>
                  {t('BehavioralPerformance')}
                </Link>
                <Link
                  to={"/" + account.platform + "/account-metrix/" + account.accountId}
                  style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', fontWeight: 600, color: 'var(--text-title)', textDecoration: 'none' }}
                  className="hover:text-[var(--stat-teal-text)] transition-colors"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m3 17 6-6 4 4 8-8"></path><path d="M14 6h7v7"></path></svg>
                  {t('MetriX')}
                </Link>
                <Link
                  to={"/" + account.platform + "/account-live-metrix/" + account.accountId}
                  style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', fontWeight: 600, color: 'var(--text-title)', textDecoration: 'none' }}
                  className="hover:text-[var(--stat-teal-text)] transition-colors"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><path d="M14 2v6h6"></path><path d="M9 13h6M9 17h6"></path></svg>
                  {t('LiveMetrix')}
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
      <Modal
        title={t('ImportTradeHistory')}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsModalOpen(false)}>
            {t('Cancel')}
          </Button>,
          <Button
            key="import"
            type="primary"
            loading={isImporting}
            onClick={handleImport}
            style={{ background: 'var(--primary-btn-bg)', borderColor: 'var(--primary-btn-bg)', color: 'var(--primary-btn-text)', fontWeight: 700 }}
          >
            {t('Import')}
          </Button>
        ]}
      >
        <div style={{ padding: '10px 0' }}>
          <p style={{ marginBottom: '15px', color: 'var(--text-desc)' }}>
            Import trade history for account <strong>{selectedAccountId}</strong>
          </p>
          <Upload.Dragger
            name="file"
            multiple={false}
            beforeUpload={(rcFile) => {
              setFile({
                uid: Math.random().toString(36).substring(7),
                name: rcFile.name,
                originFileObj: rcFile,
              });
              return false;
            }}
            onRemove={() => setFile(null)}
            fileList={file ? [file] : []}
          >
            <p className="ant-upload-drag-icon">
              <CloudUploadOutlined style={{ color: '#f2a93b' }} />
            </p>
            <p className="ant-upload-text">{t('Click or drag file to this area to upload')}</p>
            <p className="ant-upload-hint">{t('Support for a single excel file.')}</p>
          </Upload.Dragger>
        </div>
      </Modal>
    </div>
  );
};

export default AccountListView;
